import { useCallback, useEffect, useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  FormatSize,
  InsertLink,
  LinkOff,
  FormatUnderlined,
} from "@mui/icons-material";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import CopySvg from "../../assets/common/copy.svg";
import { styl } from "./RichTextEditor.styles";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { createMentionExtension } from "./Mention";

const MenuBar: React.FC<{ editor: any; handleCopy: () => void, hideCopyButton:boolean }> = ({
  editor,
  handleCopy,
  hideCopyButton,
}) => {
  const theme = useTheme();
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <Box className="flex flex-row justify-between items-start w-full editor-toolbar">
      <Box
        sx={{
          display: "flex",
          gap: "12px",
          [theme.breakpoints.down("lg")]: {
            gap: "6px",
          },
          padding: "2px",
          justifyContent: "flex-start",
        }}
      >
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`Text-Editor-Menu-Item ${
            editor.isActive("bold") ? "is-active" : ""
          }`}
        >
          <FormatBold sx={{ fontWeight: 600 }} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`Text-Editor-Menu-Item ${
            editor.isActive("italic") ? "is-active" : ""
          }`}
        >
          <FormatItalic />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          className={`Text-Editor-Menu-Item ${
            editor.isActive("strike") ? "is-active" : ""
          }`}
        >
          <FormatStrikethrough />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`Text-Editor-Menu-Item ${
            editor.isActive("underline") ? "is-active" : ""
          }`}
        >
          <FormatUnderlined />
        </button>
        <Divider orientation="vertical" flexItem />
        <button
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={`Text-Editor-Menu-Item ${
            editor.isActive("paragraph") ? "is-active" : ""
          }`}
        >
          <FormatSize />
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className={`Text-Editor-Menu-Item ${
            editor.isActive("heading", { level: 1 }) ? "is-active" : ""
          }`}
        >
          <b>H1</b>
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={`Text-Editor-Menu-Item ${
            editor.isActive("heading", { level: 2 }) ? "is-active" : ""
          }`}
        >
          <b>H2</b>
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          className={`Text-Editor-Menu-Item ${
            editor.isActive("heading", { level: 3 }) ? "is-active" : ""
          }`}
        >
          <b>H3</b>
        </button>
        <Divider orientation="vertical" flexItem />

        <button
          onClick={setLink}
          className={editor.isActive("link") ? "is-active" : ""}
        >
          <InsertLink />
        </button>
        <button
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive("link")}
        >
          <LinkOff />
        </button>

        <Divider orientation="vertical" flexItem />
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`Text-Editor-Menu-Item ${
            editor.isActive("bulletList") ? "is-active" : ""
          }`}
        >
          <FormatListBulleted />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`Text-Editor-Menu-Item ${
            editor.isActive("orderedList") ? "is-active" : ""
          }`}
        >
          <FormatListNumbered />
        </button>

        {/* <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`Text-Editor-Menu-Item ${
          editor.isActive("blockquote") ? "is-active" : ""
        }`}
      >
        blockquote
      </button> */}
      {!hideCopyButton && (<Box
        className="flex flex-row justify-end items-center gap-1 cursor-pointer self-center"
        sx={{ paddingRight: "5px" }}
        onClick={handleCopy}
      >
        <img src={CopySvg} />
        <Typography
          variant="body1"
          sx={{
            fontFamily:'var(--font-family)',
            fontSize: "13px",
            [theme.breakpoints.down("lg")]: {
              fontSize: "12px",
            },
          }}
        >
          Copy Notes
        </Typography>
      </Box>)}
      </Box>
    </Box>
  );
};

const RichTextEditor: React.FC<{
  id?: string;
  placeholder?: string;
  content?: any;
  onContentUpdate?: (content: any) => void;
  clearContent?: boolean;
  updateOnState?: boolean;
  isEditable?: boolean;
  hideMenu?: boolean;
  hidePretext?: boolean;
  setHideMenu?: React.Dispatch<React.SetStateAction<boolean>>;
  setHidePretext?: React.Dispatch<React.SetStateAction<boolean>>;
  isActive?: boolean;
  isDefaultHidingBorder?: boolean;
  hideCopyButton?: boolean;
  toolbarPosition?: "top";
  parentPage?: string;
  activeUsers?: any;
  footer?: React.ReactNode;
  showFooter?: boolean;
  autoFocus?: boolean;
  editorRef?: (editor: any | null) => void;
  height?: string;
}> = ({
  id,
  placeholder,
  content,
  onContentUpdate,
  clearContent,
  updateOnState,
  isEditable = true,
  hideMenu = false,
  hidePretext = true,
  setHideMenu,
  setHidePretext,
  isActive = false,
  isDefaultHidingBorder = false,
  hideCopyButton = false,
  parentPage,
  activeUsers,
  footer,
  showFooter = false,
  autoFocus,
  editorRef,
  height,
}) => {
  
  const extensions = useMemo(() => {
    const starterKit =
      parentPage === "action_items_bulk"
        ? StarterKit.configure({
            bulletList: false,
            orderedList: false,
            listItem: false,
          })
        : StarterKit;
  return [
    starterKit,
    Placeholder.configure({
      placeholder: placeholder || "Start writing …",
    }),
    Underline,
    Link,
    createMentionExtension(activeUsers || []),
  ];
}, [activeUsers, placeholder, parentPage]);

  // const debouncedUpdate = useCallback(
  //   debounce((documentHTMLContent) => {
  //     onContentUpdate(documentHTMLContent);
  //   }, 1000),
  //   []
  // );

  const editor = useEditor({
    extensions,
    content: content ?? "",
    parseOptions: {
      preserveWhitespace: "full",
    },
    onUpdate: ({ editor }) => {
      if (editor.isFocused) {
        onContentUpdate && onContentUpdate(!editor.isEmpty ? editor.getHTML() : "");
      }
    },
  },[extensions]);

  useEffect(() => {
    if (!editor || !updateOnState) return;

    // If user is actively typing, don't reset the document/selection.
    if (editor.isFocused) return;

    const { from, to } = editor.state.selection;
    editor.commands.setContent(content ?? "", true);
    editor.commands.setTextSelection({ from, to });
  }, [content, editor, updateOnState]);

  // 1) Sync incoming `content` -> tiptap safely (only when different)
useEffect(() => {
  if (!editor) return;

  const incoming = content ?? "";
  // if both empty, nothing to do
  if (incoming === "" && editor.isEmpty) return;

  const current = editor.getHTML() ?? "";
  if (incoming !== current) {
    try {
      const { from, to } = editor.state.selection;
      editor.commands.setContent(incoming, false, { preserveWhitespace: "full" });
      // try to restore selection; may fail if invalid for the new doc
      try { editor.commands.setTextSelection({ from, to }); } catch (e) {}
    } catch (err) {
      // fallback: set content without selection restore
      editor.commands.setContent(incoming, false, { preserveWhitespace: "full" });
    }
  }
}, [content, editor]);

// 2) Move clearContent into an effect (don't call during render)
useEffect(() => {
  if (!editor) return;
  if (clearContent) {
    editor.commands.clearContent();
  }
}, [clearContent, editor]);

// 3) Keep isEditable sync effect (include editor in deps)
useEffect(() => {
  if (!editor) return;
  editor.options.editable = isEditable;
}, [isEditable, editor]);

useEffect(() => {
  if (editorRef) {
    editorRef(editor ?? null);
  }
  return () => {
    editorRef?.(null);
  };
}, [editor, editorRef]);


  const { showSnackbar } = useDisplaySnackbar();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editor?.getText() || "");
      showSnackbar("success", "copied to clipboard");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };
  
  const items = [
    { id: 1, label: "Looks good!", icon: "🎉" },
    { id: 2, label: "Need help?", icon: "👋" },
    { id: 3, label: "Blocked...", icon: "🚫" },
    { id: 4, label: "Can you clarify...?", icon: "🔍" },
    { id: 5, label: "This is On track", icon: "✅" },
  ];
  useEffect(() => {
    if (!editor || !autoFocus || !isEditable) return;
    const id = setTimeout(() => {
      editor.commands.focus("end");
    }, 30);

    return () => clearTimeout(id);
  }, [editor, autoFocus, isEditable]);

  const isActionItemNotes = parentPage === "actionItemNotes";
  const editorHeight = isActionItemNotes && isEditable ? "auto" : hideMenu ? "auto" : "300px";
  const isBulkTaskEditor = parentPage === "action_items_bulk";

  return (
    <>
      {isEditable ? (
        <Box
          className={`flex flex-col justify-between items-start gap-2 w-full ${
            placeholder === "Take notes" ? "notes-rich-text-editor" : "rich-text-editor"
          }`}
          sx={{
            ...styl.scrollableTableCellStyle,
            '& ol, & ul': {
              paddingLeft: '2em',
              margin: 0,
            },
           minHeight: height || "",
           height: editorHeight,
           position: "relative", 
           ...(parentPage === "ActionItems"
            ? {
                borderTop: "1px solid #d1d5db",
                borderBottom: "1px solid #d1d5db",
                borderLeft: "none",
                borderRight: "none",
              }
            : {
                border: isDefaultHidingBorder
                  ? isActive
                    ? "1px solid #d1d5db"
                    : "none"
                  : "1px solid #d1d5db",
              }),
           borderRadius: isBulkTaskEditor ? "6px 6px 0px 0px" : parentPage === "ActionItems"? "0px" : "8px",
          }}      
        >
          {!["actionItemNotes", "idp-add-note"].includes(parentPage || "") && !hideMenu && (
            <Box  className="editor-toolbar">
              <MenuBar editor={editor} handleCopy={handleCopy} hideCopyButton={hideCopyButton} />
            </Box>
          )}
          <EditorContent
            id={id}
            editor={editor}
            style={{ width: "100%", padding: "10px", paddingBottom: "0px", fontFamily: 'var(--font-family)',
              height: parentPage === "actionItemNotes" ? "150px" : "auto", fontSize: "13px", overflowY: "auto"
             }}
            onClick={() => {
              setHideMenu && setHideMenu(false);
              setHidePretext && setHidePretext(true);
            }
          }
          />
          {(parentPage === "actionItemNotes" || parentPage === "idp-add-note") && (!hideMenu || showFooter) && (
            <Box className="menubar_bottom"
            >
              {!hideMenu && (
                <MenuBar
                  editor={editor}
                  handleCopy={handleCopy}
                  hideCopyButton={hideCopyButton}
                />
              )}
              {showFooter && (
                <Box sx={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                  {footer}
                </Box>
              )}
            </Box>
          )}
              {!hidePretext && editor?.isEmpty && <Box className="status-container">
                {items.map((item) => (
                  <Box key={item.id} className="status-item" onClick={() => {
                    editor?.chain().focus().insertContent(`${item.icon} ${item.label}`).run();
                    setHidePretext && setHidePretext(true);
                    setHideMenu && setHideMenu(false);
                    const html = editor?.getHTML() ?? "";
                    onContentUpdate && onContentUpdate(html);
                  }}
                  >
                    {item.icon}
                    <Typography variant="body1">{item.label}</Typography>
                  </Box>
                ))}
              </Box>}
        </Box>
      ) : (
        <EditorContent id={id} editor={editor} style={{ width: "100%", padding: "10px" }} />
      )}
    </>
  );
};

export default RichTextEditor;
